import { z } from 'zod';

import { PrecisionSchema, type Precision } from './predicate.js';
import { ViewerIdSchema, type ViewerId } from './viewer.js';

/**
 * Fact op payload kinds — the substrate stores any of these as the
 * payload of an op (envelope is OpRecord from @dl/protocol). The model
 * layer dispatches on `kind` to compute current-value rollups.
 *
 * Per-fact version history is the chain itself (00-vision.md operating
 * commitments + 02-architecture.md §4.1) — rollback is a fresh edit op
 * with the historical value, NEVER a history rewrite.
 */

/**
 * The lifecycle status of a fact (03-information-model.md §4, §7;
 * 07-session-experience.md §8).
 *
 * - `canon` — ratified by the GM. Eligible for reveal to non-GM Viewers.
 * - `pending` — session-time auto-ratification state for player-authored
 *   drafts. Visible to non-GM Viewers (subject to the standard reveal
 *   filter) AS IF canon, but tagged for GM post-session cleanup; the
 *   projection emits an additional `pending: true` flag on the resulting
 *   `VisibleFact` so the UI can render a "awaiting GM cleanup" chip. The
 *   LLM never authors `pending` directly — `pending` is reserved for
 *   player-originated content (07 §8.6 reserves a Phase 2 LLM-pending
 *   allowlist; this substrate is the plumbing that lets that flow exist
 *   without a future migration). I.A.5 (#97) plumbs the status; the
 *   emitter side (player authorship) ships in Phase 2.
 * - `draft` — proposed (LLM, GM scratch, or player suggestion). Invisible
 *   to all non-GM Viewers regardless of reveal state.
 * - `hypothetical` — GM-authored "what if". Invisible to non-GM Viewers
 *   AND to Preview-as-Viewer previews.
 */
export const FactStatusSchema = z.enum(['canon', 'pending', 'draft', 'hypothetical']);
export type FactStatus = z.infer<typeof FactStatusSchema>;

/** Default status applied when a fact-create op carries no explicit status. */
export const DEFAULT_FACT_STATUS: FactStatus = 'canon';

/**
 * Fact owner discriminated union (03-information-model.md §4, §9).
 *
 * - `canon` — objective truth.
 * - `belief` — a fact held by a Viewer (Character or Faction) that may
 *   contradict canon. Projects only into that Viewer's perspective; the
 *   phantom-Viewer invariant must hold (a belief owned by Viewer A is
 *   invisible to Viewer B unless explicitly shared — sharing is a Phase 2
 *   surface).
 *
 * **Author vs Owner is intentional**: `author` (op-level, on OpRecord) is
 * WHO wrote the op. `owner` (fact-level, here) is WHOSE perspective the
 * fact belongs to. The GM may author a belief owned by an NPC.
 */
export const FactOwnerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('canon') }).strict(),
  z.object({ kind: z.literal('belief'), viewer: ViewerIdSchema }).strict(),
]);
export type FactOwner = z.infer<typeof FactOwnerSchema>;

/** Default owner applied when a fact-create op carries no explicit owner. */
export const DEFAULT_FACT_OWNER: FactOwner = { kind: 'canon' };

/**
 * Validity range on a fact (03-information-model.md §6; ADR-0005 §4). Bounds
 * are canonical-day-count integers stored half-open `[from, to)`; calendar-pack
 * conversion lives in II.C (@dl/calendar). Either bound may be omitted:
 *
 * - both unset → timeless (always valid)
 * - `from` only → valid from that day forward, forever
 * - `to` only → valid from "always" until that day (exclusive)
 * - both set → half-open range `[from, to)`
 *
 * **Precision hints (ADR-0005 §5).** Each bound MAY carry an optional precision
 * marker (`from_precision` / `to_precision`, the same `Precision` enum the
 * `game_date` value kind uses). The stored `from` / `to` **stays an exact
 * canonical day** — range/membership queries (`validityCoversDay`) never read
 * precision, so resolution at `t` is unchanged and strictly **binary** (no
 * tri-state ever reaches projection/reveal). Precision is *editorial only*: it
 * drives DISPLAY ("the 1060s" vs "3 Hammer 1063") and MODULATES overlap-audit
 * severity (segments whose contested bounds share a precision band soft-warn
 * instead of hard-error). A fuzzy bound anchors deterministically to the exact
 * stored day (the start of the named period the picker chose). Precision on a
 * bound that is itself absent is meaningless and rejected by the refinement.
 */
export const FactValiditySchema = z
  .object({
    from: z.number().int().optional(),
    to: z.number().int().optional(),
    from_precision: PrecisionSchema.optional(),
    to_precision: PrecisionSchema.optional(),
  })
  .strict()
  .refine(
    (v) => v.from === undefined || v.to === undefined || v.from <= v.to,
    { message: 'validity.from must be <= validity.to' },
  )
  .refine((v) => v.from_precision === undefined || v.from !== undefined, {
    message: 'validity.from_precision requires validity.from',
  })
  .refine((v) => v.to_precision === undefined || v.to !== undefined, {
    message: 'validity.to_precision requires validity.to',
  });
export type FactValidity = z.infer<typeof FactValiditySchema>;

/**
 * The two ends of a {@link FactValidity}. `validityBound` projects the flat
 * `{ from, from_precision, to, to_precision }` storage shape into the
 * conceptual `{ day, precision? }` bound the ADR-0005 §5 ticket describes —
 * keeping a single source of truth (the flat fields stay authoritative; this is
 * a pure read view, never a parallel store).
 */
export type ValidityBoundSide = 'from' | 'to';

/** A resolved validity bound: an exact day plus an optional precision hint. */
export interface ValidityBound {
  day: number;
  precision?: Precision;
}

/**
 * Read one end of a validity range as a `{ day, precision? }` bound, or `null`
 * when that end is open (timeless in that direction). The stored day is always
 * exact; `precision` is the optional editorial hint (ADR-0005 §5).
 */
export function validityBound(
  validity: FactValidity | null | undefined,
  side: ValidityBoundSide,
): ValidityBound | null {
  if (validity === null || validity === undefined) return null;
  const day = side === 'from' ? validity.from : validity.to;
  if (day === undefined) return null;
  const precision = side === 'from' ? validity.from_precision : validity.to_precision;
  return precision === undefined ? { day } : { day, precision };
}

/**
 * The review state of a fact (#251, 04-llm-role.md §14.2). **Orthogonal to
 * `status`**: a bulk-generated fact accepted by a GM is `status: 'canon'` AND
 * `review_state: 'unreviewed_bulk'` simultaneously. It is GM-only provenance
 * ("this canon fact came in via a bulk-accept; give it a closer look") — it
 * NEVER reaches the projection: an `unreviewed_bulk` canon fact projects
 * identically to any other canon fact (the projection folds `status` exactly
 * as before). Clearing it is an ordinary `fact.edit` that drops the field.
 *
 * A single-value enum (not a boolean) so future review markers — e.g. an
 * `unreviewed_import` for a migrated batch — extend it without a schema break.
 */
export const FactReviewStateSchema = z.enum(['unreviewed_bulk']);
export type FactReviewState = z.infer<typeof FactReviewStateSchema>;

/**
 * Optional fact-level metadata accepted on every fact op payload. Fields
 * are optional on the wire to keep the op envelope minimal; the projection
 * applies defaults (DEFAULT_FACT_STATUS / DEFAULT_FACT_OWNER) when folding
 * the chain, which is what lets pre-status/pre-owner ops back-fill cleanly
 * at projection-rebuild time without rewriting the op log.
 */
const FactMetaShape = {
  status: FactStatusSchema.optional(),
  owner: FactOwnerSchema.optional(),
  /**
   * Belief provenance (`03-information-model.md §9`). When a belief is CLONED
   * from another belief (NPC tells PC a lie), the clone carries the source
   * belief's predicate key here — forming a provenance chain (Drunkard →
   * Rogue → PC) so "where did this rumor originate?" is a deterministic query,
   * not GM memory. Only meaningful on belief-owned facts; ignored otherwise.
   */
  source_belief: z.string().min(1).optional(),
  /**
   * Suggestion-set bundle link (#251). The `bun_…` id of the bundle this fact
   * was generated in — set on each member draft `fact.create` so the bundle
   * fold (`foldBundles`) can group members back to their `bundle.declare`. The
   * generation metadata (prompt/depth/mode/…) lives ONCE on the declare op, not
   * here — this is only the back-pointer. Absent on hand-authored facts.
   */
  bundle_id: z.string().min(1).optional(),
  /**
   * Bulk-review marker (#251, §14.2). GM-only provenance, ORTHOGONAL to
   * `status` — never consulted by projection. Present means "needs review";
   * cleared by a plain `fact.edit` dropping the field. See {@link FactReviewStateSchema}.
   */
  review_state: FactReviewStateSchema.optional(),
  /**
   * LLM self-rated confidence in this fact, 0–1 (#251, §14.6). LLM-authored per
   * draft; GM-only — it drives sort/threshold in the bundle-review UI and never
   * reaches projection. Absent on hand-authored facts.
   */
  confidence: z.number().min(0).max(1).optional(),
  // validity is tri-state on the wire:
  //   - absent (undefined) → "preserve the previous validity" (used by
  //     value-only edits, e.g. retyping a name — they must not wipe an
  //     existing range).
  //   - a range object       → set to that range.
  //   - explicit null        → "clear to timeless". This is the path the
  //     validity editor's open-ended/clear flow takes; without an
  //     explicit null the fold can't distinguish "clear" from "don't
  //     touch" and clearing silently no-ops (the fold's
  //     `if (validity !== undefined)` guard preserves the old range).
  validity: FactValiditySchema.nullable().optional(),
};

export const FactCreatePayloadSchema = z
  .object({
    kind: z.literal('fact.create'),
    value: z.unknown(),
    ...FactMetaShape,
  })
  .strict();
export type FactCreatePayload = z.infer<typeof FactCreatePayloadSchema>;

export const FactEditPayloadSchema = z
  .object({
    kind: z.literal('fact.edit'),
    value: z.unknown(),
    ...FactMetaShape,
  })
  .strict();
export type FactEditPayload = z.infer<typeof FactEditPayloadSchema>;

export const FactSoftDeletePayloadSchema = z
  .object({
    kind: z.literal('fact.soft_delete'),
  })
  .strict();
export type FactSoftDeletePayload = z.infer<typeof FactSoftDeletePayloadSchema>;

export const FactRestorePayloadSchema = z
  .object({
    kind: z.literal('fact.restore'),
  })
  .strict();
export type FactRestorePayload = z.infer<typeof FactRestorePayloadSchema>;

/**
 * Status-mutation op. Used to ratify a draft (`status: 'canon'`), retract
 * a ratified fact (`status: 'draft'`), or shelve as hypothetical
 * (`status: 'hypothetical'`). The op log carries the transition; the
 * projection folds the chain into the current status.
 *
 * `reason` is optional GM-supplied audit text (03-information-model.md §7
 * "Ratification is reversible").
 */
export const FactSetStatusPayloadSchema = z
  .object({
    kind: z.literal('fact.set_status'),
    status: FactStatusSchema,
    reason: z.string().min(1).optional(),
  })
  .strict();
export type FactSetStatusPayload = z.infer<typeof FactSetStatusPayloadSchema>;

export type FactPayload =
  | FactCreatePayload
  | FactEditPayload
  | FactSoftDeletePayload
  | FactRestorePayload
  | FactSetStatusPayload;

export const FactPayloadSchema = z.discriminatedUnion('kind', [
  FactCreatePayloadSchema,
  FactEditPayloadSchema,
  FactSoftDeletePayloadSchema,
  FactRestorePayloadSchema,
  FactSetStatusPayloadSchema,
]);

/** Optional meta fields accepted by `factCreate` / `factEdit` builders. */
export interface FactMetaInput {
  status?: FactStatus;
  owner?: FactOwner;
  /**
   * Tri-state (see `FactMetaShape`): omit to preserve, pass a range to
   * set, pass `null` to explicitly clear to timeless.
   */
  validity?: FactValidity | null;
  /** Source belief's predicate key when this belief was cloned (03 §9). */
  source_belief?: string;
  /** The `bun_…` bundle this fact was generated in (#251). */
  bundle_id?: string;
  /** Bulk-review marker (#251 §14.2) — GM-only, orthogonal to `status`. */
  review_state?: FactReviewState;
  /** LLM self-rated confidence 0–1 (#251 §14.6) — GM-only. */
  confidence?: number;
}

/** Build a create-fact payload. Meta fields are optional; absence implies defaults. */
export function factCreate(value: unknown, meta: FactMetaInput = {}): FactCreatePayload {
  return { kind: 'fact.create', value, ...meta };
}

/** Build an edit-fact payload (rollback uses this with a historical value). */
export function factEdit(value: unknown, meta: FactMetaInput = {}): FactEditPayload {
  return { kind: 'fact.edit', value, ...meta };
}

/** Build a soft-delete-fact payload. The fact remains in the op chain. */
export function factSoftDelete(): FactSoftDeletePayload {
  return { kind: 'fact.soft_delete' };
}

/** Build a restore-fact payload, reversing a prior soft-delete. */
export function factRestore(): FactRestorePayload {
  return { kind: 'fact.restore' };
}

/** Build a status-mutation op (ratify, retract, shelve as hypothetical). */
export function factSetStatus(status: FactStatus, reason?: string): FactSetStatusPayload {
  return reason === undefined
    ? { kind: 'fact.set_status', status }
    : { kind: 'fact.set_status', status, reason };
}

/** Build a belief-owner descriptor. */
export function beliefOwner(viewer: ViewerId): FactOwner {
  return { kind: 'belief', viewer };
}

/**
 * The folded current state of a fact chain: the latest value (or `null`
 * if absent / soft-deleted), plus the current `status`, `owner`, and
 * `validity`. Pre-status / pre-owner ops back-fill via DEFAULT_FACT_STATUS
 * and DEFAULT_FACT_OWNER — the op log is never rewritten.
 */
export interface FoldedFact {
  value: unknown | null;
  status: FactStatus;
  owner: FactOwner;
  validity: FactValidity | null;
  /** True if currently soft-deleted (value is null even if previously set). */
  soft_deleted: boolean;
}

/**
 * Fold an HLC-ordered chain of fact ops into the current folded fact state.
 * Caller is responsible for ordering the chain in HLC order before calling.
 *
 * `fact.create` sets the value (and seeds status/owner/validity from the
 *   payload, defaulting absent fields).
 * `fact.edit` updates the value; meta fields, if present, replace the
 *   current values; if absent, the prior values carry through.
 * `fact.soft_delete` clears the current value (rollup as absent).
 * `fact.restore` undoes the most recent soft-delete.
 * `fact.set_status` updates only the status (value/owner/validity are
 *   unchanged).
 */
export function foldFactChainFull(chain: readonly FactPayload[]): FoldedFact | null {
  let value: unknown | null = null;
  let status: FactStatus = DEFAULT_FACT_STATUS;
  let owner: FactOwner = DEFAULT_FACT_OWNER;
  let validity: FactValidity | null = null;
  let deleted = false;
  let everCreated = false;

  for (const payload of chain) {
    switch (payload.kind) {
      case 'fact.create':
        value = payload.value;
        status = payload.status ?? DEFAULT_FACT_STATUS;
        owner = payload.owner ?? DEFAULT_FACT_OWNER;
        validity = payload.validity ?? null;
        deleted = false;
        everCreated = true;
        break;
      case 'fact.edit':
        value = payload.value;
        if (payload.status !== undefined) status = payload.status;
        if (payload.owner !== undefined) owner = payload.owner;
        if (payload.validity !== undefined) validity = payload.validity;
        deleted = false;
        break;
      case 'fact.soft_delete':
        deleted = true;
        break;
      case 'fact.restore':
        deleted = false;
        break;
      case 'fact.set_status':
        status = payload.status;
        break;
    }
  }

  if (!everCreated && chain.length === 0) return null;
  if (!everCreated) {
    // A chain of only set_status / soft_delete / restore without a create
    // is malformed — treat as absent.
    return null;
  }

  return {
    value: deleted ? null : value,
    status,
    owner,
    validity,
    soft_deleted: deleted,
  };
}

/**
 * Back-compat fold: returns just the current value (or `null` if absent or
 * soft-deleted), preserving the Walking-Skeleton signature. Callers needing
 * status / owner / validity should switch to `foldFactChainFull`.
 */
export function foldFactChain(chain: readonly FactPayload[]): unknown | null {
  const folded = foldFactChainFull(chain);
  if (!folded) return null;
  return folded.soft_deleted ? null : folded.value;
}
